import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, Clock, KeyRound, LogOut, Play, Wifi, WifiOff, Sun, Moon, Check, X } from 'lucide-react'
import { useState } from 'react'
import { Link, Outlet } from 'react-router-dom'
import { ApiError, engineApi, systemApi } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useTheme } from '../hooks/useTheme'
import { useIsMobile } from '../hooks/useMediaQuery'
import { useWebSocket } from '../hooks/useWebSocket'
import type { WebSocketHookReturn } from '../hooks/useWebSocket'
import { TimeframeProvider } from '../hooks/useTimeframe'
import { SanityGateBufferProvider } from '../hooks/useSanityGateBuffer'
import { SafetyStrip } from './SafetyStrip'
import { BookSwitcher } from './BookSwitcher'
import { TimeframeSwitcher } from './TimeframeSwitcher'
import { MobileChrome } from './MobileChrome'
import { ModuleDrawer } from './desk/ModuleDrawer'
import './Dashboard.css'

/**
 * Context handed to every routed page via <Outlet context={...} />.
 * Pages read it with `useOutletContext<AppShellContext>()`.
 */
export interface AppShellContext {
  ws: WebSocketHookReturn
}

/**
 * The one and only WebSocket owner. useWebSocket() opens a brand-new
 * connection on every call — it is NOT a singleton — so exactly one
 * component in the tree may call it. AppShell is that component; every
 * page below it receives the single instance via Outlet context.
 *
 * Also owns the global header (workflow trigger, market guard, WS
 * indicator, theme toggle, auth controls) — relocated verbatim from the
 * old Dashboard.tsx — and the BookSwitcher nav beneath it.
 */
export function AppShell() {
  const ws = useWebSocket()
  const queryClient = useQueryClient()
  const { user, logout } = useAuth()
  const { theme, toggle: toggleTheme } = useTheme()
  // MOBILE_BP — keep in sync with the `@media (max-width: 767px)` CSS tier.
  // WARGAME 18 D2: below this width the desktop chrome (SafetyStrip,
  // header, .shell-switcher-row) is swapped for MobileChrome via the
  // fragment ternary in the return below. The desktop JSX branch stays
  // byte-identical to what it was before this branch existed —
  // `git diff -w` over that region should show only whitespace reindent.
  const isMobile = useIsMobile()
  // Engine status — used to surface an "Auto" chip next to the Run Workflow
  // button when the engine is in SCHEDULED (or CONTINUOUS) mode so the
  // operator sees at a glance that cycles are firing on their own. Polled at
  // the same cadence as CloudWatch so a transient pause is visible within ~1m.
  const engineStatusQuery = useQuery({
    queryKey: ['engine-status'],
    queryFn: () => engineApi.getStatus(),
    refetchInterval: 60000,
  })
  const engineStatus = engineStatusQuery.data
  const autonomousMode =
    engineStatus?.status === 'running' &&
    (engineStatus.workflow_mode === 'scheduled' ||
      engineStatus.workflow_mode === 'continuous')

  // System health drives the market-closed confirm-on-click warning below.
  // /system/health is unauthed-stable and already polled by other components;
  // we share the cache via the 'system-health' key so we don't double-fetch.
  // 60s refetch matches the engine-status cadence — market open/close events
  // are minute-aligned anyway.
  const systemHealthQuery = useQuery({
    queryKey: ['system-health'],
    queryFn: () => systemApi.getHealth(),
    refetchInterval: 60000,
  })
  const marketStatus = systemHealthQuery.data?.market_status
  // Treat unknown / loading as "open" — never block the operator on
  // missing health data; the worst case is they run a manual cycle that
  // SanityGate will reject on stale_data anyway.
  const marketClosed =
    marketStatus !== undefined && marketStatus !== 'open' && marketStatus !== 'unknown'

  const [runStatus, setRunStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'success'; runId: string; durationMs: number }
    | { kind: 'ran-failed'; runId: string; error: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' })

  // Workflow trigger — calls POST /api/v1/engine/workflow/execute.
  // Autonomy principle: HTTP 200 with success=false means the workflow RAN but
  // produced a no-go/failed outcome (normal observable result).  Surface it to
  // the operator as a ran-but-failed warning, not a thrown error.  A network or
  // 5xx response (mutation onError) means a genuine server/extreme failure and
  // is surfaced distinctly.
  const triggerWorkflowMutation = useMutation({
    mutationFn: () => engineApi.executeWorkflow(),
    onSuccess: (data) => {
      if (data.success) {
        setRunStatus({
          kind: 'success',
          runId: data.run_id,
          durationMs: data.duration_ms,
        })
      } else {
        // Workflow ran but produced a failed outcome — normal in the autonomous
        // system; the SanityGate/auto-stop handles it in later phases.
        setRunStatus({
          kind: 'ran-failed',
          runId: data.run_id,
          error: data.error ?? 'Workflow cycle failed',
        })
      }
      // Refresh signal/position state regardless of success — the cycle may
      // have produced partial fills before failing.
      queryClient.invalidateQueries({ queryKey: ['pending-signals'] })
      queryClient.invalidateQueries({ queryKey: ['positions'] })
      queryClient.invalidateQueries({ queryKey: ['system-health'] })
    },
    onError: (err: unknown) => {
      // Network error or genuine server failure (5xx) — operator attention needed.
      const message =
        err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      setRunStatus({ kind: 'error', message })
    },
  })

  return (
    <TimeframeProvider>
      <SanityGateBufferProvider ws={ws}>
      <div className="dashboard">
        {isMobile ? (
          <MobileChrome
            ws={ws}
            engineStatus={engineStatus}
            marketClosed={marketClosed}
            marketStatus={marketStatus}
            runStatus={runStatus}
            onRunWorkflow={() => triggerWorkflowMutation.mutate()}
            isRunning={triggerWorkflowMutation.isPending}
          />
        ) : (
          <>
        <SafetyStrip />

        <header className="dashboard-header">
          <div className="header-left">
            <div className="logo">
              <Activity size={24} />
              Trading Platform
            </div>
          </div>

          <div className="header-right">
            <div className="workflow-trigger">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  // Market-closed guard: a manual cycle outside trading hours
                  // will still execute end-to-end but SanityGate is very
                  // likely to reject every signal on stale_data, and any
                  // signal that survives can't be filled until the next
                  // open. Confirm before paying the round-trip.
                  if (marketClosed) {
                    const ok = window.confirm(
                      `Market is currently ${marketStatus}. Running a workflow ` +
                      `cycle will execute the full pipeline but most signals ` +
                      `will be rejected on stale_data and no orders will fill ` +
                      `until the market reopens.\n\nRun anyway?`,
                    )
                    if (!ok) return
                  }
                  setRunStatus({ kind: 'idle' })
                  triggerWorkflowMutation.mutate()
                }}
                disabled={triggerWorkflowMutation.isPending}
                title={
                  marketClosed
                    ? `Market is ${marketStatus}. Click to confirm a manual cycle anyway.`
                    : autonomousMode
                      ? `Engine is autonomous (${engineStatus?.workflow_mode}). This button runs one extra cycle on demand.`
                      : 'Run one manual workflow cycle (MANUAL mode)'
                }
              >
                <Play size={14} />
                {triggerWorkflowMutation.isPending ? 'Running…' : 'Run Workflow'}
              </button>
              {autonomousMode && (
                <span
                  className="workflow-auto-chip"
                  title={
                    engineStatus?.next_workflow_run
                      ? `Next scheduled cycle: ${engineStatus.next_workflow_run}`
                      : `Autonomous mode: ${engineStatus?.workflow_mode}`
                  }
                >
                  <span className="auto-dot" aria-hidden="true" />
                  <Clock size={12} aria-hidden="true" />
                  Auto · {engineStatus?.workflow_mode}
                </span>
              )}
              {marketClosed && (
                <span
                  className="workflow-market-chip"
                  title={`Market is currently ${marketStatus}. Manual cycles will mostly reject on stale_data.`}
                >
                  Market · {marketStatus}
                </span>
              )}
              {runStatus.kind === 'success' && (
                <span className="workflow-trigger-status success">
                  <Check size={14} aria-hidden="true" />
                  {runStatus.runId} ({(runStatus.durationMs / 1000).toFixed(2)}s)
                </span>
              )}
              {runStatus.kind === 'ran-failed' && (
                <span className="workflow-trigger-status error">
                  <X size={14} aria-hidden="true" />
                  Cycle ran, failed: {runStatus.error} ({runStatus.runId})
                </span>
              )}
              {runStatus.kind === 'error' && (
                <span className="workflow-trigger-status error">
                  <X size={14} aria-hidden="true" />
                  Server error: {runStatus.message}
                </span>
              )}
            </div>
            <div className="connection-indicator">
              {ws.isConnected ? (
                <>
                  <Wifi size={16} />
                  <span className="ws-dot connected" />
                </>
              ) : (
                <>
                  <WifiOff size={16} />
                  <span className="ws-dot disconnected" />
                </>
              )}
              <span>{ws.isConnected ? 'Connected' : 'Disconnected'}</span>
            </div>
            <button
              type="button"
              className="theme-toggle focus-ring"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="auth-control">
              <Link
                to="/agent-tokens"
                className="btn btn-secondary btn-sm"
                title="Manage agent API tokens"
              >
                <KeyRound size={14} />
                Agent Tokens
              </Link>
              {user && <span className="auth-user">{user.username}</span>}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  void logout()
                }}
                title="Sign out"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </div>
        </header>

        <div className="shell-switcher-row">
          <BookSwitcher />
          <TimeframeSwitcher />
        </div>
          </>
        )}

        <Outlet context={{ ws } satisfies AppShellContext} />
        {!isMobile && <ModuleDrawer ws={ws} />}
      </div>
      </SanityGateBufferProvider>
    </TimeframeProvider>
  )
}
